/**
 * V2EmbedSection — V2-native editor section for step.content.embed
 *
 * Sub-sections:
 * 1. isIframe toggle — content.embed.isIframe
 * 2. HTML Editor (Monaco) — content.embed.html (untyped)
 * 3. Data Sources (child sections) — content.embed.childSections
 *
 * Legacy inputs/outputs tables removed — replaced by structured child sections
 * that are accessible via segmentlySDK.getChildSection(name) in custom HTML.
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import Editor, { type OnMount } from '@monaco-editor/react';
import { SAccordion } from '@modules/ui-components';
import { EditorGuideProvider } from '../../../contexts';
import { SECTION_MARGIN_BOTTOM } from '../../../config/ui';
import { getValueByPath } from '../../../utils/v2FieldOperations';
import { getSectionTestId } from '../../../utils/testIdUtils';
import { ImageUrlScannerDialog } from './shared/ImageUrlScannerDialog';
import { EmbedChildSectionsManager } from './EmbedChildSectionsManager';
import { buildEmbedSdkConfig } from './embedConfigBuilder';
import { useVariableEditor } from '../../../contexts/VariableEditorContext';
import type { UrlMapping } from '../../../utils/imageUrlScanner';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { LayoutSection } from '@segmently/schema-models/v2';

const MIN_EDITOR_HEIGHT = 200;
const DEFAULT_EDITOR_HEIGHT = 300;
const EXPANDED_EDITOR_HEIGHT = 600;
const MAX_EDITOR_HEIGHT = 800;

export interface V2EmbedSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  projectId?: string;
  onboardingId?: string;
  versionId?: string;
  /** Override accordion title (used by FlexibleLayout for section labels) */
  sectionTitle?: string;
  /** Hide internal Data Sources manager (when parent renders its own) */
  hideDataSources?: boolean;
}

export const V2EmbedSection: React.FC<V2EmbedSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  projectId,
  onboardingId,
  versionId,
  sectionTitle,
  hideDataSources = false,
}) => {
  // ============================================================================
  // HTML EDITOR STATE
  // ============================================================================
  const editorRef = useRef<Parameters<OnMount>[0] | null>(null);
  const [localValue, setLocalValue] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [editorHeight, setEditorHeight] = useState(DEFAULT_EDITOR_HEIGHT);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasUnappliedChanges, setHasUnappliedChanges] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartY = useRef(0);
  const resizeStartHeight = useRef(0);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [configCopied, setConfigCopied] = useState(false);

  // Variables for config builder
  const { variables } = useVariableEditor();

  // Get embed content
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const embedContent = useMemo(() => (getValueByPath(step, 'content.embed') as any) ?? {}, [step]);
  const isIframe = embedContent?.isIframe !== false; // default true

  // Get HTML from step (untyped field)
  const htmlValue = useMemo(() => {
    return getValueByPath<string>(step, 'content.embed.html') ?? '';
  }, [step]);

  // Initialize local value
  useEffect(() => {
    if (!isInitialized) {
      setLocalValue(htmlValue);
      setIsInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlValue, isInitialized]);

  const handleEditorChange = useCallback(
    (value: string | undefined) => {
      const newValue = value ?? '';
      setLocalValue(newValue);
      setHasUnappliedChanges(newValue !== htmlValue);
    },
    [htmlValue]
  );

  const handleApply = useCallback(() => {
    onChange('content.embed.html', localValue);
    setHasUnappliedChanges(false);
  }, [onChange, localValue]);

  const handleExpandToggle = useCallback(() => {
    setIsExpanded((prev) => {
      setEditorHeight(!prev ? EXPANDED_EDITOR_HEIGHT : DEFAULT_EDITOR_HEIGHT);
      return !prev;
    });
  }, []);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartY.current = e.clientY;
    resizeStartHeight.current = editorHeight;
  }, [editorHeight]);

  useEffect(() => {
    if (!isResizing) return;
    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientY - resizeStartY.current;
      setEditorHeight(Math.max(MIN_EDITOR_HEIGHT, Math.min(MAX_EDITOR_HEIGHT, resizeStartHeight.current + delta)));
    };
    const handleMouseUp = () => setIsResizing(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleEditorMount: OnMount = useCallback((editor) => {
    editorRef.current = editor;
    editor.focus();
  }, []);

  const handleSelectAll = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const model = editor.getModel();
    if (!model) return;
    editor.setSelection(model.getFullModelRange());
    editor.focus();
  }, []);

  const handleScannerApplyReplacements = useCallback(
    (newHtml: string, _mappings: UrlMapping[]) => {
      setLocalValue(newHtml);
      onChange('content.embed.html', newHtml);
      setHasUnappliedChanges(false);
      setScannerOpen(false);
    },
    [onChange]
  );

  // ============================================================================
  // CHILD SECTIONS (Data Sources)
  // ============================================================================
  const childSections = useMemo(
    () => (embedContent?.childSections as LayoutSection[] | undefined) ?? [],
    [embedContent],
  );

  /**
   * Handle Media DataSource creation from image scanner.
   * Merges new sections with existing childSections, handling overwrites.
   */
  const handleCreateDataSources = useCallback(
    (newSections: LayoutSection[]) => {
      let updated = [...childSections];

      for (const section of newSections) {
        // Check for _replaceLabel marker (overwrite mode)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const replaceLabel = (section as any)._replaceLabel as string | undefined;
        if (replaceLabel) {
          // Remove old section with matching label
          updated = updated.filter(
            s => !((s.kind as string) === 'Media' && (s.label ?? '').toLowerCase() === replaceLabel.toLowerCase())
          );
        }
        // Clean the marker before adding
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { _replaceLabel: _, ...cleanSection } = section as any;
        updated.push(cleanSection as LayoutSection);
      }

      onChange('content.embed.childSections', updated);
    },
    [childSections, onChange],
  );

  // ============================================================================
  // GET CONFIG (copy SDK config to clipboard for AI agents)
  // ============================================================================
  const handleGetConfig = useCallback(async () => {
    const config = buildEmbedSdkConfig({
      screenId: step.entityId,
      screenName: (step as unknown as Record<string, unknown>).name as string | undefined,
      screenType: step.screenType || 'WebEmbed',
      childSections,
      locale,
      variables,
    });
    try {
      await navigator.clipboard.writeText(JSON.stringify(config, null, 2));
      setConfigCopied(true);
      setTimeout(() => setConfigCopied(false), 2000);
    } catch {
      // Fallback: prompt user with text
      const w = window.open('', '_blank', 'width=600,height=400');
      if (w) {
        w.document.write(`<pre>${JSON.stringify(config, null, 2)}</pre>`);
      }
    }
  }, [step, childSections, locale, variables]);

  // Virtual section wrapper for EmbedChildSectionsManager
  const virtualSection = useMemo(() => ({
    id: step.entityId,
    kind: 'CustomEmbed' as LayoutSection['kind'],
    order: 0,
    layout: {} as LayoutSection['layout'],
    embedContent: { ...embedContent, childSections },
  }), [step.entityId, embedContent, childSections]);

  // Write updated sections array back via onChange
  const handleWriteSections = useCallback(
    (updatedSections: LayoutSection[]) => {
      // EmbedChildSectionsManager calls writeSections with the full top-level array;
      // the updated virtual section contains the new childSections.
      const updatedVirtual = updatedSections.find(s => s.id === step.entityId);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newChildren = (updatedVirtual as any)?.embedContent?.childSections ?? [];
      onChange('content.embed.childSections', newChildren);
    },
    [onChange, step.entityId],
  );

  const SECTION_NAME = 'Embed';

  return (
    <>
      {/* HTML Editor */}
      <SAccordion
        title={sectionTitle ?? SECTION_NAME}
        collapsible
        defaultExpanded={defaultExpanded}
        sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
        data-testid={getSectionTestId('Embed')}
      >
        <EditorGuideProvider>
          {/* isIframe toggle */}
          <FormControlLabel
            data-testid="embed-iframe-row"
            label={
              <Typography variant="caption" color="text.secondary">
                Render in iframe (full isolation)
              </Typography>
            }
            control={
              <Switch
                size="small"
                checked={isIframe}
                onChange={(e) => onChange('content.embed.isIframe', e.target.checked)}
                disabled={disabled}
                data-testid="embed-iframe-toggle"
              />
            }
            sx={{ mb: 1 }}
          />

          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              Write HTML, CSS, and JavaScript. Click &quot;Apply&quot; to preview.
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Select all code">
                <IconButton size="small" onClick={handleSelectAll} disabled={disabled}>
                  <SelectAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={isExpanded ? 'Collapse editor' : 'Expand editor'}>
                <IconButton size="small" onClick={handleExpandToggle}>
                  {isExpanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              {projectId && onboardingId && versionId && (
                <Tooltip title="Scan & upload external images to CDN">
                  <IconButton
                    size="small"
                    onClick={() => setScannerOpen(true)}
                    disabled={disabled || !localValue.trim()}
                    data-testid="embed-scan-images-btn"
                  >
                    <ImageSearchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Monaco Editor */}
          <Box
            data-testid="embed-html-editor"
            sx={{
              border: '1px solid',
              borderColor: hasUnappliedChanges ? 'warning.main' : 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              opacity: disabled ? 0.5 : 1,
              pointerEvents: disabled ? 'none' : 'auto',
              transition: 'border-color 0.2s',
            }}
          >
            <Editor
              height={editorHeight}
              defaultLanguage="html"
              value={localValue}
              onChange={handleEditorChange}
              onMount={handleEditorMount}
              loading={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: editorHeight }}>
                  <CircularProgress size={24} />
                </Box>
              }
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                lineNumbers: 'on',
                wordWrap: 'on',
                automaticLayout: true,
                scrollBeyondLastLine: false,
                tabSize: 2,
                readOnly: disabled,
                renderWhitespace: 'selection',
                bracketPairColorization: { enabled: true },
                folding: true,
                lineDecorationsWidth: 8,
                lineNumbersMinChars: 3,
              }}
              theme="vs-dark"
            />
          </Box>

          {/* Resize Handle */}
          <Box
            onMouseDown={handleResizeStart}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 16,
              cursor: 'ns-resize',
              bgcolor: isResizing ? 'action.selected' : 'transparent',
              '&:hover': { bgcolor: 'action.hover' },
              userSelect: 'none',
            }}
          >
            <DragHandleIcon sx={{ fontSize: 18, color: 'text.secondary', transform: 'rotate(90deg)' }} />
          </Box>

          {/* Apply Button */}
          <Button
            variant={hasUnappliedChanges ? 'contained' : 'outlined'}
            color={hasUnappliedChanges ? 'primary' : 'inherit'}
            onClick={handleApply}
            disabled={disabled}
            startIcon={<PlayArrowIcon />}
            fullWidth
            size="small"
            sx={{ mt: 0.5 }}
          >
            Apply to Preview
          </Button>

          <Box sx={{ mt: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              Tip: Access data sources via <code>segmentlySDK.getChildSection(&apos;name&apos;)</code>,
              text via <code>segmentlySDK.getChildSectionText(&apos;name&apos;)</code>,
              and navigate with <code>segmentlySDK.navigateNext(data)</code>
            </Typography>
            <Tooltip title={configCopied ? 'Copied!' : 'Copy SDK config to clipboard (for AI agents)'}>
              <Button
                size="small"
                variant="outlined"
                color={configCopied ? 'success' : 'inherit'}
                onClick={handleGetConfig}
                startIcon={configCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                sx={{ whiteSpace: 'nowrap', minWidth: 'auto', flexShrink: 0 }}
              >
                {configCopied ? 'Copied' : 'Get Config'}
              </Button>
            </Tooltip>
          </Box>

          {/* Image URL Scanner Dialog */}
          {projectId && onboardingId && versionId && (
            <ImageUrlScannerDialog
              open={scannerOpen}
              onClose={() => setScannerOpen(false)}
              htmlContent={localValue}
              uploadContext={{ projectId, onboardingId, versionId, locale }}
              onApplyReplacements={handleScannerApplyReplacements}
              useMultiSizeUpload
              locale={locale}
              existingChildSections={childSections}
              onCreateDataSources={handleCreateDataSources}
            />
          )}
        </EditorGuideProvider>
      </SAccordion>

      {/* Data Sources (Child Sections) — hidden when parent manages its own.
          Wrapped in a testid'd Box so the help-article block recipe can clip the
          whole Data Sources region (the manager itself carries no testid). */}
      {!hideDataSources && (
        <Box data-testid="embed-data-sources">
          <EmbedChildSectionsManager
            section={virtualSection as unknown as LayoutSection}
            sections={[virtualSection as unknown as LayoutSection]}
            writeSections={handleWriteSections}
            locale={locale}
            baseStep={step}
            disabled={disabled}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            showVariableBinding
          />
        </Box>
      )}
    </>
  );
};
